'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * useLastScrollDirection
 *
 * Returns the last scroll direction as a string: 'top' (scrolling up) or 'bottom' (scrolling down).
 * The value persists until the user scrolls in the opposite direction, so you can use it to
 * show/hide UI based on whether the user was last scrolling up or down.
 *
 * @param {number} [scrollMargin=200] - Minimum pixels the user must scroll before the direction
 *                                     is updated. Avoids direction changes on tiny movements.
 * @returns {'top' | 'bottom' | null} - 'top' when last scroll was upward, 'bottom' when last scroll
 *          was downward, or null before any scroll has been detected.
 */
export default function useLastScrollDirection(scrollMargin = 200) {
  const [lastDirection, setLastDirection] = useState(null)
  const lastScrollY = useRef(0)
  const ticking = useRef(false)

  useEffect(() => {
    const updateScrollDir = () => {
      const scrollY = window.scrollY || window.pageYOffset
      const delta = scrollY - lastScrollY.current

      if (Math.abs(delta) >= scrollMargin) {
        if (delta > 0) {
          setLastDirection('bottom')
        } else {
          setLastDirection('top')
        }
        lastScrollY.current = scrollY >= 0 ? scrollY : 0
      }
      ticking.current = false
    }

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDir)
        ticking.current = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [scrollMargin])

  return lastDirection
}
